import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { AdAccountLinks, ShopifyLinkStatusDTO } from 'services/user/ad-account-links'
import { parseError } from 'utils/errors'

export type ShopifyStep = 'install' | 'embed' | 'connected'
export type ShopifyPhase = 'idle' | 'installing' | 'enabling'

const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 3 * 60 * 1000

// Bare handle → full myshopify domain; anything with a dot is passed through (the backend
// re-normalizes and enforces the *.myshopify.com format). Kept for the claim page's shop param.
export const normalizeShop = (input: string): string => {
	const raw = input.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '')
	if (!raw) return ''
	return raw.includes('.') ? raw : `${raw}.myshopify.com`
}

// A fresh Shopify install starts from Shopify's side (the App Store install link — OAuth can't begin
// without a shop), so ClickGUARD can't pass the target asset through the OAuth `state`. Instead we
// stash the asset the merchant was on when they clicked Install; the claim page reads it back (same
// browser) and auto-binds the store to it, so no picking is needed (DEV-2365 §2.3).
const CLAIM_INTENT_KEY = 'shopify_claim_intent'
const CLAIM_INTENT_TTL_MS = 60 * 60 * 1000

export interface ShopifyClaimIntent {
	accountId: string
	assetId: string
	ts: number
}

export const stashShopifyClaimIntent = (accountId: string, assetId: string): void => {
	try {
		window.localStorage.setItem(CLAIM_INTENT_KEY, JSON.stringify({ accountId, assetId, ts: Date.now() }))
	} catch {
		/* localStorage unavailable — the claim page falls back to the manual asset picker */
	}
}

export const readShopifyClaimIntent = (): ShopifyClaimIntent | null => {
	try {
		const raw = window.localStorage.getItem(CLAIM_INTENT_KEY)
		if (!raw) return null
		const intent = JSON.parse(raw) as ShopifyClaimIntent
		if (!intent?.accountId || !intent?.assetId) return null
		// Stale intent (merchant abandoned then installed hours later) → ignore, use the picker.
		if (Date.now() - (intent.ts || 0) > CLAIM_INTENT_TTL_MS) return null
		return intent
	} catch {
		return null
	}
}

export const clearShopifyClaimIntent = (): void => {
	try {
		window.localStorage.removeItem(CLAIM_INTENT_KEY)
	} catch {
		/* best-effort */
	}
}

// The OAuth popup relays failures via the `shopify_oauth_error` localStorage key (set by the
// /oauth/shopify result page). Turn the backend's reason into something the merchant can read.
const friendlyShopifyError = (reason: string): string => {
	if (reason === 'access_denied') return 'Shopify authorization was declined. Please try again.'
	if (reason === 'shopify_connect_failed') return 'We couldn’t connect your Shopify store. Please try again.'
	return reason
}

// Where the wizard should sit for a given link. Drives both first-time connect and the
// already-connected steady state (settings) from one source of truth.
const stepForLink = (link: ShopifyLinkStatusDTO | null | undefined): ShopifyStep => {
	if (!link || link.status === 'DISCONNECTED') return 'install' // not connected → show the install button
	if (link.status === 'NEEDS_REAUTH') return 'install' // installed but token dead → reconnect
	return link.embed_active ? 'connected' : 'embed'
}

export interface UseShopifyConnectArgs {
	accountId: string
	assetId: string
	// Pre-fetched status. undefined → the hook self-fetches; null/DTO → skip the fetch + loader.
	initialLink?: ShopifyLinkStatusDTO | null
}

export interface ShopifyConnect {
	loading: boolean
	link: ShopifyLinkStatusDTO | null
	step: ShopifyStep
	phase: ShopifyPhase
	error: string | null
	busy: boolean
	installed: boolean
	embedActive: boolean
	needsReauth: boolean
	setError: (v: string | null) => void
	installApp: () => Promise<void>
	enableEmbed: () => void
	cancel: () => void
	disconnect: () => Promise<void>
}

/**
 * The full Shopify connect state machine, shared by the settings and onboarding wrappers so the
 * polling / OAuth-relay / reconnect / embed logic lives in exactly one place. The wrappers own only
 * layout + copy.
 *
 * Flow: install (App Store → Shopify OAuth) → embed (theme editor) → connected. A fresh install
 * launches from the Shopify App Store (see installApp); reconnect (NEEDS_REAUTH) knows the store, so
 * it goes straight to OAuth. Disconnect is handled from the same link state.
 */
export function useShopifyConnect({ accountId, assetId, initialLink }: UseShopifyConnectArgs): ShopifyConnect {
	const [loading, setLoading] = useState(initialLink === undefined)
	const [link, setLink] = useState<ShopifyLinkStatusDTO | null>(initialLink ?? null)
	const [step, setStep] = useState<ShopifyStep>(stepForLink(initialLink))
	const [phase, setPhase] = useState<ShopifyPhase>('idle')
	const [error, setError] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)
	// Prefetched so the "Install app" click can window.open() synchronously (no await → no popup block).
	const [installUrl, setInstallUrl] = useState<string | null>(null)

	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const mountedRef = useRef(true)

	const stopPolling = () => {
		if (pollRef.current) {
			clearInterval(pollRef.current)
			pollRef.current = null
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}

	// Apply a fresh link everywhere: state + derived step.
	const applyLink = (fresh: ShopifyLinkStatusDTO | null) => {
		setLink(fresh)
		setStep(stepForLink(fresh))
	}

	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
			stopPolling()
		}
	}, [])

	// Prefetch the Shopify-side install link (from the backend shopify config) so the button can open
	// it synchronously on click. Best-effort — installApp fetches on demand if this hasn't landed yet.
	useEffect(() => {
		let cancelled = false
		AdAccountLinks.getShopifyInstallUrl(accountId)
			.then((r) => {
				if (!cancelled) setInstallUrl(r.install_url)
			})
			.catch(() => {
				/* installApp fetches on demand as a fallback */
			})
		return () => {
			cancelled = true
		}
	}, [accountId])

	// Fetch + reset whenever the asset/account changes (topbar switch), so the wizard reloads for the
	// new asset just like the other tracking sections. The first render for a host-prefetched link is
	// skipped — we're already seeded from `initialLink` (avoids a redundant fetch + loader flash).
	const didInit = useRef(false)
	useEffect(() => {
		if (!didInit.current && initialLink !== undefined) {
			didInit.current = true
			return
		}
		didInit.current = true

		let cancelled = false
		stopPolling()
		setPhase('idle')
		setError(null)
		setLoading(true)
		;(async () => {
			try {
				const fresh = await AdAccountLinks.getShopifyLink(accountId, assetId)
				if (cancelled || !mountedRef.current) return
				applyLink(fresh)
			} catch {
				if (!cancelled && mountedRef.current) {
					setLink(null)
					setStep('install')
				}
			} finally {
				if (!cancelled && mountedRef.current) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [accountId, assetId])

	// A freshly-installed (or legacy) ACTIVE link may not have its embed status resolved yet — do it
	// once so the wizard lands on the right step (embed vs connected) instead of guessing.
	useEffect(() => {
		if (!link || link.status !== 'ACTIVE' || link.embed_active !== null) return
		let cancelled = false
		;(async () => {
			try {
				const fresh = await AdAccountLinks.recheckShopify(accountId, link.id)
				if (cancelled || !mountedRef.current) return
				applyLink(fresh)
			} catch {
				if (!cancelled && mountedRef.current) applyLink({ ...link, embed_active: false })
			}
		})()
		return () => {
			cancelled = true
		}
	}, [link, accountId])

	// Poll for the OAuth result: watch the localStorage relay for a fast failure, otherwise poll the
	// link until it goes ACTIVE. Shared by first install and reconnect.
	const startInstallPoll = () => {
		stopPolling()
		pollRef.current = setInterval(async () => {
			const relayErr = window.localStorage.getItem('shopify_oauth_error')
			if (relayErr) {
				window.localStorage.removeItem('shopify_oauth_error')
				if (!mountedRef.current) return
				stopPolling()
				setPhase('idle')
				setError(friendlyShopifyError(relayErr))
				return
			}
			try {
				const fresh = await AdAccountLinks.getShopifyLink(accountId, assetId)
				if (!mountedRef.current || !fresh || fresh.status === 'DISCONNECTED') return
				stopPolling()
				setPhase('idle')
				// Connection detected — the claim intent has served its purpose; drop it so it can't
				// leak into an unrelated app-open later.
				clearShopifyClaimIntent()
				applyLink(fresh)
			} catch {
				/* transient — keep polling */
			}
		}, POLL_INTERVAL_MS)
		timeoutRef.current = setTimeout(() => {
			stopPolling()
			if (!mountedRef.current) return
			setPhase('idle')
			setError('We didn’t detect the install. If you approved it on Shopify, refresh — otherwise try again.')
		}, POLL_TIMEOUT_MS)
	}

	// Kick off an install/reconnect. Two paths:
	//   • Reconnect (we already know the store): go straight to Shopify OAuth for that shop — the
	//     backend builds an authorize URL bound to this asset, and the popup relays via /oauth/shopify.
	//   • Fresh install (no store yet): OAuth needs a shop we don't have, so launch from the Shopify
	//     App Store. We stash the target asset first; the store OAuths in on Shopify's side and the
	//     claim page auto-binds it back to this asset (DEV-2365 §2.3).
	const installApp = async () => {
		setError(null)
		// Drop any stale relay from a previous attempt so it can't trip the new poll loop.
		window.localStorage.removeItem('shopify_oauth_error')

		const knownShop = link?.shop_domain
		if (knownShop) {
			setBusy(true)
			try {
				const { authorize_url } = await AdAccountLinks.shopifyAuthorize(accountId, {
					shop: knownShop,
					asset_id: assetId
				})
				window.open(authorize_url, '_blank')
				setPhase('installing')
				startInstallPoll()
			} catch (err) {
				const message = parseError(err) || 'Could not start the Shopify connection'
				setError(message)
				toast.error(message)
			} finally {
				setBusy(false)
			}
			return
		}

		// Fresh install → launch the Shopify-side install. Remember which asset to bind the store to on
		// return. Use the prefetched install URL for a synchronous open; fetch on demand if it's not in
		// yet (rare — costs an await, but keeps the flow working).
		stashShopifyClaimIntent(accountId, assetId)
		let url = installUrl
		if (!url) {
			setBusy(true)
			try {
				url = (await AdAccountLinks.getShopifyInstallUrl(accountId)).install_url
			} catch (err) {
				const message = parseError(err) || 'Could not start the Shopify install'
				setError(message)
				toast.error(message)
				return
			} finally {
				setBusy(false)
			}
		}
		window.open(url, '_blank')
		setPhase('installing')
		startInstallPoll()
	}

	const enableEmbed = () => {
		if (!link) return
		setError(null)
		// Open the theme editor deep link; poll until the embed is detected on (or the token dies).
		window.open(link.embed_activate_url, '_blank')
		setPhase('enabling')
		stopPolling()
		pollRef.current = setInterval(async () => {
			try {
				const fresh = await AdAccountLinks.recheckShopify(accountId, link.id)
				if (!mountedRef.current) return
				if (fresh.embed_active) {
					stopPolling()
					setPhase('idle')
					applyLink(fresh)
				} else if (fresh.status === 'NEEDS_REAUTH') {
					stopPolling()
					setPhase('idle')
					applyLink(fresh)
				}
			} catch {
				/* keep polling */
			}
		}, POLL_INTERVAL_MS)
		timeoutRef.current = setTimeout(() => {
			stopPolling()
			if (mountedRef.current) setPhase('idle')
		}, POLL_TIMEOUT_MS)
	}

	const cancel = () => {
		stopPolling()
		setPhase('idle')
		// Abandoned mid-flow — drop the stashed intent so a later app-open can't pick it up.
		clearShopifyClaimIntent()
	}

	const disconnect = async () => {
		if (!link) return
		setBusy(true)
		try {
			await AdAccountLinks.disconnectShopify(accountId, link.id)
			if (!mountedRef.current) return
			setLink(null)
			setError(null)
			setStep('install')
			toast.success('Shopify store disconnected')
		} catch (err) {
			toast.error(parseError(err) || 'Could not disconnect the Shopify store')
		} finally {
			setBusy(false)
		}
	}

	return {
		loading,
		link,
		step,
		phase,
		error,
		busy,
		installed: !!link && (link.status === 'ACTIVE' || link.status === 'NEEDS_REAUTH'),
		embedActive: link?.embed_active === true,
		needsReauth: link?.status === 'NEEDS_REAUTH',
		setError,
		installApp,
		enableEmbed,
		cancel,
		disconnect
	}
}
